import { useMemo } from 'react'
import { AddressBar } from '@/components/ui/address-bar'
import type { OpenTab } from '@/components/features/document-tabs'
import type { BasecampBreadcrumbItem } from '@/lib/basecamp-breadcrumbs'
import {
  basecampBreadcrumbItemsToAddressBarSegments,
  buildBasecampProjectHomeTrail,
  resolveBasecampTabProjectId,
} from '@/lib/basecamp-breadcrumbs'

interface BasecampIntegrationBreadcrumbsProps {
  currentTab: OpenTab
  containerWidth?: number | null
  /** When false, omit the current (last) segment - used in list viewer header where title is shown separately. */
  includeCurrent?: boolean
}

const SECTION_LABELS: Record<string, string> = {
  projects: 'Projects',
  todos: 'Todos',
  bookmarks: 'Bookmarks',
  mystuff: 'My Stuff',
}

/** A todo list's own name is the toolbar heading, same as a todo. Drop that crumb so the trail stays the path. Ancestors stay clickable. */
function trailForToolbar(
  items: BasecampBreadcrumbItem[],
  tabType: string | undefined,
): { items: BasecampBreadcrumbItem[]; includeCurrent: boolean } {
  const last = items[items.length - 1]
  const listIsCurrent =
    tabType === 'integration-basecamp-todo-list' &&
    (last?.type === 'todolist' || last?.type === 'section')
  return { items, includeCurrent: !listIsCurrent }
}

export function BasecampIntegrationBreadcrumbs({
  currentTab,
  containerWidth = null,
}: BasecampIntegrationBreadcrumbsProps) {
  const segments = useMemo(() => {
    const section = currentTab.integrationData?.section as string | undefined
    const projectId = resolveBasecampTabProjectId(currentTab)

    // Section hubs (Projects / Todos / Bookmarks list)
    if (section && !projectId) {
      const label = SECTION_LABELS[section] || section
      return basecampBreadcrumbItemsToAddressBarSegments(
        [{ id: section, title: label, type: 'projects' }],
        {
          projectId: '',
          projectName: '',
          organizationId: currentTab.integrationData?.organizationId,
          workspaceId: currentTab.workspaceId,
        },
        { includeCurrent: true },
      )
    }

    if (!projectId) return []

    const cachedName = localStorage.getItem(`basecamp-project-name:${projectId}`)
    const projectName =
      currentTab.integrationData?.projectName || cachedName || 'Basecamp Project'

    const trail = currentTab.integrationData?.breadcrumbTrail as
      | BasecampBreadcrumbItem[]
      | undefined

    if (trail && trail.length > 0) {
      const hasProjectsCrumb = trail.some((i) => i.type === 'projects')
      const normalized = hasProjectsCrumb
        ? trail
        : [
            { id: 'projects', title: 'Projects', type: 'projects' as const },
            ...trail,
          ]

      const toolbarTrail = trailForToolbar(normalized, currentTab.type)

      return basecampBreadcrumbItemsToAddressBarSegments(
        toolbarTrail.items,
        {
          projectId,
          projectName,
          organizationId: currentTab.integrationData?.organizationId,
          workspaceId: currentTab.workspaceId,
        },
        { includeCurrent: toolbarTrail.includeCurrent },
      )
    }

    // Project home (no trail yet / enrich pending)
    if (currentTab.type === 'integration-basecamp') {
      return basecampBreadcrumbItemsToAddressBarSegments(
        buildBasecampProjectHomeTrail(projectId, projectName),
        {
          projectId,
          projectName,
          organizationId: currentTab.integrationData?.organizationId,
          workspaceId: currentTab.workspaceId,
        },
        { includeCurrent: true },
      )
    }

    const listName =
      currentTab.integrationData?.listName || currentTab.title || 'Todo List'
    const fallback = trailForToolbar(
      [
        { id: 'projects', title: 'Projects', type: 'projects' },
        { id: projectId, title: projectName, type: 'project' },
        { id: 'todoset', title: 'To-Dos', type: 'todoset' },
        {
          id: currentTab.integrationData?.listId || 'list',
          title: listName,
          type: 'todolist',
        },
      ],
      currentTab.type,
    )
    return basecampBreadcrumbItemsToAddressBarSegments(
      fallback.items,
      {
        projectId,
        projectName,
        organizationId: currentTab.integrationData?.organizationId,
        workspaceId: currentTab.workspaceId,
      },
      { includeCurrent: fallback.includeCurrent },
    )
  }, [currentTab])

  if (segments.length === 0) return null

  return <AddressBar segments={segments} containerWidth={containerWidth} />
}
